import type { ContractInput, PlanCalcOptions } from './calculations.js';
import { createSellRateResolver, leafAmount } from './calculations.js';
import type { PlanNode, ProjectDocument } from './project-document.js';
import { rewritePlan } from './project-document.js';

/**
 * Task → contract mapping as pure document transforms (D8, D9, D17).
 *
 * Mapping is explicit: a leaf bills through a contract only by its own
 * `contractId`, and nothing falls back to a project default or a parent. The
 * two operations here are what the UI runs when the user decides — connecting
 * every unmapped task to the first contract, and previewing what a remap will
 * do to a task's staffing lines before it happens. Both are pure so the copy
 * the dialogs show is assertable, and both go through the ordinary save path.
 */

export type AssignContractResult = {
  document: ProjectDocument;
  /** Leaves that received the contract (they named none before). */
  assignedNodeIds: string[];
};

/**
 * D9 — "connect all tasks to this contract": every leaf that names no
 * contract gets `contractId`. Already-mapped leaves are left alone, parents
 * carry no mapping (BR-2), and the project prefill is set too so tasks created
 * from now on follow. Iterative (BR-3).
 */
export const assignContractToUnmapped = (
  document: ProjectDocument,
  contractId: string,
): AssignContractResult => {
  const assignedNodeIds: string[] = [];
  const { plan } = rewritePlan(document.plan, (node) => {
    if (node.children.length > 0 || node.contractId !== undefined) return node;
    assignedNodeIds.push(node.id);
    return { ...node, contractId };
  });
  return {
    document: { ...document, defaultContractId: contractId, plan },
    assignedNodeIds,
  };
};

/** How many leaves name no contract — what the first-contract prompt reports. */
export const countUnmappedLeaves = (document: ProjectDocument): number => {
  let count = 0;
  const stack = [...document.plan];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node.children.length === 0) {
      if (node.contractId === undefined) count++;
    } else {
      stack.push(...node.children);
    }
  }
  return count;
};

export type RemapPreview = {
  /** Staffing lines whose role the target contract cannot price (D17). */
  orphanedLines: { allocationId: string; roleName: string; hours: number }[];
  amountBefore: number;
  amountAfter: number;
};

/**
 * D17 — what remapping one leaf to `toContractId` (or to none) would do:
 * which staffing lines lose their price because the target's rate card lacks
 * their role, and how the leaf's planned revenue moves. A fixed-price target
 * prices every role at its average rate, so it never orphans a line; no
 * contract orphans nothing either — it simply prices nothing (D7).
 */
export const previewContractRemap = (
  node: PlanNode,
  contracts: ContractInput[],
  blendedCostRate: number,
  toContractId: string | undefined,
  options?: PlanCalcOptions,
): RemapPreview => {
  const resolve = createSellRateResolver(contracts, blendedCostRate, options);
  const target =
    toContractId === undefined ? undefined : contracts.find((c) => c.id === toContractId);
  const orphanedLines =
    target === undefined || target.type === 'fixed'
      ? []
      : node.allocations
          .filter(
            (line) => resolve(target.id, line.roleRef, line.personRef).state === 'missing-role',
          )
          .map((line) => ({
            allocationId: line.id,
            roleName: line.roleRef.snapshot.name,
            hours: line.hours,
          }));
  const remapped: PlanNode =
    toContractId === undefined
      ? (({ contractId: _dropped, ...rest }) => rest)(node)
      : { ...node, contractId: toContractId };
  return {
    orphanedLines,
    amountBefore: leafAmount(node, resolve),
    amountAfter: leafAmount(remapped, resolve),
  };
};
